import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors, TailwindColorValue } from '@repo/tailwind-config/tailwind.vars'
import { FC, useEffect, useRef, useState } from 'react'

import { XIcon } from '../../icons'
import ProgressBar from './progress-bar'
import Typography from './typography'

export type QueueBatchStatusEnum = 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED'

export type BatchProgressData = {
  batchId: number
  totalJobs: number
  completedJobs: number
  failedJobs: number
  progress: number
  status: QueueBatchStatusEnum
  errorMessage?: string | null
  resultUrl?: string
}

type BatchProgressTrackerProps = {
  title: string
  batch: BatchProgressData
  onDismiss?: () => void
}

const BatchProgressTracker: FC<BatchProgressTrackerProps> = ({ batch, title, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isExiting, setIsExiting] = useState(false)
  const isComplete = batch.progress === 100
  const isFailed = batch.failedJobs > 0 || batch.status === 'FAILED'
  // A completed batch can still carry a message (e.g. centers missing from a PLI ZIP)
  const hasWarning = !isFailed && !!batch.errorMessage
  const ref = useRef<HTMLAnchorElement>(null)

  // Animate in on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    // Keep the tracker open so the warning is read, the user downloads from the link below
    if (isComplete && batch.resultUrl && !hasWarning) {
      // Automatically trigger download after a short delay
      const timer = setTimeout(() => {
        ref.current?.click()
        onDismiss?.()
      }, 1000) // 1 second delay before triggering download

      return () => clearTimeout(timer)
    }
  }, [isComplete, batch.resultUrl, hasWarning])

  const handleDismiss = () => {
    setIsExiting(true)
    setTimeout(() => {
      onDismiss?.()
    }, 300)
  }

  const adjustedProgress = (progress: number) => {
    // remove decimals and round to nearest integer
    return Math.min(100, Math.max(0, Math.floor(progress)))
  }

  const color: TailwindColorValue = isComplete ? 'green' : isFailed ? 'red' : 'primary'

  return (
    <div
      className={cn(
        isVisible && !isExiting ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
      )}
    >
      <div className="bg-gradient-to-r shadow-lg border-b border-grey-light bg-white rounded-lg shadow">
        <div className="max-w-5xl mx-auto px-6 py-4">
          {!isFailed ? (
            <>
              <ProgressBar
                height={2}
                text={'complétées'}
                reverted
                title={title}
                animationMs={800}
                onIconClick={handleDismiss}
                progress={adjustedProgress(batch.progress)}
                colorStart={tailwindColors[color].light.hover}
                colorEnd={tailwindColors[color].DEFAULT}
              />
              {hasWarning && (
                <Typography
                  variant="tag"
                  color={tailwindColors.orange.DEFAULT}
                  className="text-sm"
                >
                  {batch.errorMessage}
                </Typography>
              )}
              <a
                ref={ref}
                href={batch.resultUrl}
                rel="noopener noreferrer"
                className={cn(
                  hasWarning && isComplete && batch.resultUrl ? 'inline-flex' : 'hidden',
                  'items-center gap-1.5 text-sm font-medium text-primary hover:text-primary-dark transition-colors'
                )}
              >
                {hasWarning && 'Télécharger'}
              </a>
            </>
          ) : (
            <div className="flex items-center justify-between">
              <Typography variant="tag" color={tailwindColors.red.DEFAULT} className="text-sm">
                {title} - {batch.errorMessage || 'Une erreur est survenue pendant le traitement.'}
              </Typography>
              <button
                onClick={handleDismiss}
                className="ml-2 text-grey hover:text-grey-dark transition-colors"
                aria-label="Dismiss"
              >
                <XIcon color={tailwindColors.grey.lighter} width="18px" height="18px" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BatchProgressTracker
